import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';
import { IconDefinition, faClock, faUsersCrown } from '@fortawesome/pro-duotone-svg-icons';
import { faExchangeAlt } from '@fortawesome/pro-solid-svg-icons';
import { Select, Store } from '@ngxs/store';
import moment from 'moment';
import { Observable, interval } from 'rxjs';
import { map } from 'rxjs/operators';
import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebBrick } from 'src/app/components/bricks/samweb-brick.decorator';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { BrickLocationType } from 'src/app/samwebDescription/samweb/brickLocationType';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { BrickSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/brick-settings.state';
import { CommonBrickSettingsSelectors } from 'src/app/store/dashboard/brick-wall/brick-settings/common-brick-settings/common-brick-settings.selectors';
import { StageSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/stage-settings/stage-settings.state';
import { ToolbarSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/toolbar-settings/toolbar-settings.state';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { SamwinLinesState } from 'src/app/store/samwin/samwin-lines/samwin-lines.state';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { SamwinUserStatusState } from 'src/app/store/samwin/samwin-user-status/samwin-user-status.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { SessionState } from 'src/app/store/session/session.state';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SamwinLineStatusRemoteDto } from 'src/generated/signalR/samwin-line-status-remote-dto';
import { UserStatusInfoRemoteDto } from 'src/generated/signalR/user-status-info-remote-dto';
import { StageSettingsComponent } from './stage-settings/stage-settings.component';

@Component({
   selector: 'samweb-stage-brick',
   templateUrl: './stage-brick.component.html',
   styleUrls: ['./stage-brick.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
@SamwebBrick({
   brickDefinitionId: 'stageBrick',
   brickTitleTranslationKey: 'brick.brick_definitions.stage_brick.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.stage_brick.description',
   brickIcon: faUsersCrown,
   minColWidth: 29,
   minRowHeight: 10,
   brickCategory: BrickCategory.CALLING,
   settingsComponent: StageSettingsComponent,
   settingStateHandler: StageSettingsState,
   requiredFeature: FeatureEnum.SAMWIN_7_AGENT,
   allowedBrickLocation: BrickLocationType.BRICKWALL,
   requiresActiveCoreConnection: true,
})
export class StageBrickComponent extends BasicBrickComponent implements OnInit, AfterViewInit {
   constructor(private store: Store, private sanitizer: DomSanitizer) {
      super();
   }

   @Select(SamwinUserStatusState.currentStatusInfo)
   public currentUserStatusInfo$: Observable<UserStatusInfoRemoteDto>;

   @Select(SessionState.getUserFullname)
   public userFullName$: Observable<string>;

   @Select(SessionState.getUserDn)
   public userDn$: Observable<string>;

   @Select(SamwinLinesStateSelector.isCallInProgress)
   public isCallInProgress$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.getCallDirection)
   public callDirection$: Observable<number>;

   @Select(ClientPhoneState.isLineAIdle)
   public isLineAIdle$: Observable<boolean>;

   @Select(ClientPhoneState.isLineBIdle)
   public isLineBIdle$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.canToggleHoldLineB)
   public canToggleHoldLineB$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.isLineAOnHold)
   public isLineAOnHold$: Observable<boolean>;

   @Select(SamwinLinesStateSelector.isLineBOnHold)
   public isLineBOnHold$: Observable<boolean>;

   @Select(ClientPhoneState.getWaitingSinceActiveCall)
   public waitingSince$: Observable<Date>;

   @Select(SamwinLinesState.getIsConnectedSince)
   public connectedSince$: Observable<Date>;

   @Select(ClientPhoneState.isRecordingOrError)
   public isRecordingOrError$: Observable<boolean>;

   @Select(SamwinUserStatusState.getNextAutoStatusChangeTime)
   public currentUserStatusDuration$: Observable<moment.Moment>;

   @Select(SamwinLinesStateSelector.getSourceLineStateModel)
   public currentSourceLineStateModel$: Observable<SamwinLineStatusRemoteDto>;

   @Select(SamwinLinesStateSelector.getCurrentCall)
   public callInfo$: Observable<SamwinCallInfoRemoteDto>;

   @Select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_ATTENDANT))
   public featureEnabled$: Observable<boolean>;

   public now$ = interval(500).pipe(map(() => moment()));

   public hasActions$: Observable<boolean>;
   public showDefaultBackground$: Observable<boolean>;
   public showNextDialTarget$: Observable<boolean>;

   public brickTitle$: Observable<string>;
   public showCreature$: Observable<boolean>;
   public showTeeth$: Observable<boolean>;
   public showBeard$: Observable<boolean>;
   public showNinja$: Observable<boolean>;
   public showZorro$: Observable<boolean>;
   public showSporty$: Observable<boolean>;
   public showPirate$: Observable<boolean>;
   public showParty$: Observable<boolean>;

   public avatarBackgroundStyle: SafeStyle;

   public brickInstanceId: string;
   public connectedIcon: IconDefinition = faExchangeAlt;
   public clockIcon: IconDefinition = faClock;

   ngOnInit(): void {
      this.hasActions$ = this.store.select(ToolbarSettingsState.hasToolbarActions).pipe(map((fn) => fn(this.brickInstanceId, 'toolbar')));
      this.showDefaultBackground$ = this.store.select(StageSettingsState.getShowDefaultBackground).pipe(map((filterFn) => filterFn(this.brickInstanceId)));
      this.showNextDialTarget$ = this.store.select(StageSettingsState.getShowNextDialTarget).pipe(map((filterFn) => filterFn(this.brickInstanceId)));

      this.brickTitle$ = this.store.select(CommonBrickSettingsSelectors.getOverwrittenBrickTitleFn).pipe(
         map((fn) => fn(this.brickInstanceId)),
         map((value) => value?.toLowerCase())
      );

      this.showCreature$ = this.brickTitle$.pipe(map((value) => value?.includes('creature')));
      this.showTeeth$ = this.brickTitle$.pipe(map((value) => value?.includes('teeth')));
      this.showNinja$ = this.brickTitle$.pipe(map((value) => value?.includes('ninja')));
      this.showBeard$ = this.brickTitle$.pipe(map((value) => value?.includes('beard')));
      this.showZorro$ = this.brickTitle$.pipe(map((value) => value?.includes('zorro')));
      this.showSporty$ = this.brickTitle$.pipe(map((value) => value?.includes('sporty')));
      this.showPirate$ = this.brickTitle$.pipe(map((value) => value?.includes('pirate')));
      this.showParty$ = this.brickTitle$.pipe(map((value) => value?.includes('party')));
   }

   ngAfterViewInit() {
      this.store.selectSnapshot(BrickSettingsState.isBrickSettingDialogOpenForBrickInstance(this.brickInstanceId));
   }

   avatarBackgroundStyleChangedHandler(avatarBackgroundStyle: string) {
      if (avatarBackgroundStyle !== '') {
         this.avatarBackgroundStyle = this.sanitizer.bypassSecurityTrustStyle(`url(${avatarBackgroundStyle})`);
      }
   }

   skillInfo(call: SamwinCallInfoRemoteDto) {
      return this.store.selectSnapshot(SamwinSkillsState.getSkillConfigurationFn)(call.queueId);
   }
}
